import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import { SocialAuthButtons } from './LoginForm';

const EXPERIENCE_OPTIONS = [
  { value: 'beginner', label: 'Beginner', desc: 'New to investing, learning the basics' },
  { value: 'intermediate', label: 'Intermediate', desc: '1-5 years experience, familiar with stocks & funds' },
  { value: 'advanced', label: 'Advanced', desc: '5+ years, experienced with diverse asset classes' },
];

export default function RegistrationForm({ onSuccess, onSwitchToLogin }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', experience: '' });
  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showExpDropdown, setShowExpDropdown] = useState(false);
  const fileRef = useRef();

  const validate = () => {
    const e = {};
    if (!form?.name?.trim()) e.name = 'Full name is required';
    if (!form?.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(form?.email)) e.email = 'Invalid email';
    if (!form?.password) e.password = 'Password is required';
    else if (form?.password?.length < 8) e.password = 'Minimum 8 characters';
    if (form?.password !== form?.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!form?.experience) e.experience = 'Please select your experience level';
    return e;
  };

  const handlePhoto = (file) => {
    if (file && file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => setPhotoPreview(e?.target?.result);
      reader?.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault(); setIsDragging(false);
    handlePhoto(e?.dataTransfer?.files?.[0]);
  };

  const handleSubmit = (ev) => {
    ev?.preventDefault();
    const e = validate();
    if (Object.keys(e)?.length) { setErrors(e); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); onSuccess({ ...form, photo: photoPreview }); }, 1000);
  };

  const selectedExp = EXPERIENCE_OPTIONS?.find(o => o?.value === form?.experience);

  return (
    <div className="w-full">
      <h2 className="mb-1" style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', color: 'var(--color-foreground)' }}>
        Join WIN Today
      </h2>
      <p className="mb-5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)', fontSize: '0.9rem' }}>
        Start your wealth-building journey with women who invest
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Photo Upload */}
        <div>
          <label style={{ fontFamily: 'var(--font-caption)', fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-foreground)', display: 'block', marginBottom: 8 }}>
            Profile Photo <span style={{ color: 'var(--color-muted-foreground)', fontWeight: 400 }}>(optional)</span>
          </label>
          <div className="flex items-center gap-4">
            <div className="rounded-full overflow-hidden flex-shrink-0"
              style={{ width: 64, height: 64, background: 'var(--color-muted)', border: '2px solid var(--color-border)' }}>
              {photoPreview
                ? <Image src={photoPreview} alt="User uploaded profile photo preview showing selected image" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><Icon name="User" size={28} color="var(--color-muted-foreground)" /></div>
              }
            </div>
            <div
              className="flex-1 rounded-lg flex flex-col items-center justify-center gap-2 cursor-pointer transition-all"
              style={{
                border: `2px dashed ${isDragging ? 'var(--color-accent)' : 'var(--color-border)'}`,
                padding: '16px 12px',
                background: isDragging ? 'rgba(133,113,77,0.05)' : 'var(--color-surface)',
              }}
              onDragOver={e => { e?.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef?.current?.click()}>
              <Icon name="Upload" size={20} color="var(--color-accent)" />
              <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', color: 'var(--color-muted-foreground)', textAlign: 'center' }}>
                Drag & drop or <span style={{ color: 'var(--color-accent)', fontWeight: 600 }}>browse</span>
              </span>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handlePhoto(e?.target?.files?.[0])} />
            </div>
          </div>
        </div>

        <Input label="Full Name" type="text" placeholder="Sarah Johnson"
          value={form?.name} onChange={e => setForm(p => ({ ...p, name: e?.target?.value }))}
          error={errors?.name} required />
        <Input label="Email Address" type="email" placeholder="sarah@example.com"
          value={form?.email} onChange={e => setForm(p => ({ ...p, email: e?.target?.value }))}
          error={errors?.email} required />
        <Input label="Password" type="password" placeholder="Min. 8 characters"
          value={form?.password} onChange={e => setForm(p => ({ ...p, password: e?.target?.value }))}
          error={errors?.password} required />
        <Input label="Confirm Password" type="password" placeholder="Repeat your password"
          value={form?.confirmPassword} onChange={e => setForm(p => ({ ...p, confirmPassword: e?.target?.value }))}
          error={errors?.confirmPassword} required />

        {/* Experience Dropdown */}
        <div className="relative">
          <label style={{ fontFamily: 'var(--font-caption)', fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-foreground)', display: 'block', marginBottom: 6 }}>
            Investment Experience <span style={{ color: 'var(--color-error)' }}>*</span>
          </label>
          <button type="button"
            className="w-full flex items-center justify-between rounded-lg px-4 py-3 transition-all"
            style={{
              border: `1px solid ${errors?.experience ? 'var(--color-error)' : 'var(--color-border)'}`,
              background: 'var(--color-input)', fontFamily: 'var(--font-caption)', fontSize: '0.9rem',
              color: selectedExp ? 'var(--color-foreground)' : 'var(--color-muted-foreground)', cursor: 'pointer',
            }}
            onClick={() => setShowExpDropdown(p => !p)}>
            <span>{selectedExp ? selectedExp?.label : 'Select your experience level'}</span>
            <Icon name={showExpDropdown ? 'ChevronUp' : 'ChevronDown'} size={16} color="var(--color-muted-foreground)" />
          </button>
          {errors?.experience && <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', color: 'var(--color-error)', marginTop: 4 }}>{errors?.experience}</p>}
          {showExpDropdown && (
            <div className="absolute left-0 right-0 mt-1 rounded-lg overflow-hidden shadow-lg z-20"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-background)' }}>
              {EXPERIENCE_OPTIONS?.map(opt => (
                <button key={opt?.value} type="button"
                  className="w-full flex flex-col items-start px-4 py-3 transition-all text-left"
                  style={{
                    background: form?.experience === opt?.value ? 'rgba(133,113,77,0.08)' : 'transparent',
                    borderBottom: '1px solid var(--color-border)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(133,113,77,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = form?.experience === opt?.value ? 'rgba(133,113,77,0.08)' : 'transparent'}
                  onClick={() => { setForm(p => ({ ...p, experience: opt?.value })); setShowExpDropdown(false); }}>
                  <span style={{ fontFamily: 'var(--font-caption)', fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-foreground)' }}>{opt?.label}</span>
                  <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8rem', color: 'var(--color-muted-foreground)' }}>{opt?.desc}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Button variant="default" fullWidth loading={loading} type="submit" className="mt-2">
          Create My Account
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
        <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', color: 'var(--color-muted-foreground)' }}>or</span>
        <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
      </div>
      <SocialAuthButtons />
      <p className="text-center mt-6" style={{ fontFamily: 'var(--font-caption)', fontSize: '0.875rem', color: 'var(--color-muted-foreground)' }}>
        Already a member?{' '}
        <button onClick={onSwitchToLogin} style={{ color: 'var(--color-accent)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
          Sign in
        </button>
      </p>
    </div>
  );
}